import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "@/components/toggles";
import { ArrowLeft, Sparkles, ImageIcon, Video, Loader2, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AXES = [
  { key: "contamination", label: "Verschmutzung", tone: "amber" },
  { key: "moisture", label: "Feuchtigkeit", tone: "blue" },
  { key: "material_change", label: "Materialveränderung", tone: "purple" },
  { key: "deformation", label: "Deformation", tone: "orange" },
  { key: "surface_change", label: "Oberflächenänderung", tone: "pink" },
];

const STATUS_COLOR = {
  QUEUED: "text-amber-500",
  RUNNING: "text-blue-500",
  COMPLETED: "text-emerald-600",
  FAILED: "text-red-500",
  CANCELLED: "text-[#999]",
};

export default function ModulGPreviewRequestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [enrollmentActive, setEnrollmentActive] = useState(false);
  const [requests, setRequests] = useState([]);
  const [subject, setSubject] = useState(null);   // Single subject (V2)
  const [showCreate, setShowCreate] = useState(false);
  const [artifacts, setArtifacts] = useState({});   // request_id -> artifact

  // Create form
  const [poseIndex, setPoseIndex] = useState(null);   // welche Pose als Base
  const [outputType, setOutputType] = useState("IMAGE");
  const [vec, setVec] = useState({ contamination: 0.2, moisture: 0.1, material_change: 0.0, deformation: 0.05, surface_change: 0.1 });
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const e = await api.get("/modul-g/enrollment");
      const active = e.data.enrollment?.status === "ACTIVE";
      setEnrollmentActive(active);
      if (active) {
        const [r, s] = await Promise.all([
          api.get("/modul-g/preview-requests"),
          api.get("/modul-g/subject").catch(() => ({ data: null })),
        ]);
        const reqs = r.data.requests || [];
        setRequests(reqs);
        setSubject(s.data);
        if (s.data?.poses?.length && poseIndex === null) {
          const primary = s.data.poses.find((p) => p.is_primary) || s.data.poses[0];
          setPoseIndex(primary.pose_index);
        }
        const completed = reqs.filter((x) => x.status === "COMPLETED" && x.artifact_id);
        if (completed.length) {
          const results = await Promise.allSettled(
            completed.map((x) => api.get(`/modul-g/preview-requests/${x.request_id}/artifact`))
          );
          const map = {};
          completed.forEach((c, i) => {
            if (results[i].status === "fulfilled") map[c.request_id] = results[i].value.data;
          });
          setArtifacts(map);
        } else {
          setArtifacts({});
        }
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user) { navigate("/login?next=/studio/preview/requests"); return; }
    load();
    // eslint-disable-next-line
  }, [user]);

  // Auto-Poll alle 8s solange Requests laufen (QUEUED/RUNNING)
  useEffect(() => {
    if (!enrollmentActive) return;
    const inFlight = requests.some((r) => ["QUEUED", "RUNNING"].includes(r.status));
    if (!inFlight) return;
    const id = setInterval(() => { load(); }, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [enrollmentActive, requests]);

  const submitCreate = async () => {
    setCreateError("");
    if (!subject || !subject.poses?.length) { setCreateError("Kein Motiv/Pose vorhanden"); return; }
    if (poseIndex === null) { setCreateError("Bitte Pose wählen"); return; }
    setSubmitting(true);
    try {
      await api.post("/modul-g/preview-requests", {
        output_type: outputType, state_vector: vec, pose_index: poseIndex,
      });
      setShowCreate(false);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setCreateError(d?.message || d || "Fehler beim Anlegen");
    } finally { setSubmitting(false); }
  };

  const cancelReq = async (id) => {
    if (!window.confirm("Preview-Request stornieren?")) return;
    try {
      await api.post(`/modul-g/preview-requests/${id}/cancel`);
      await load();
    } catch (e) { alert(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  if (loading) return <div data-testid="mg-requests-loading" className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#666]" /></div>;

  if (!enrollmentActive) {
    return (
      <div data-testid="mg-requests-gated" className="min-h-screen bg-white p-6 flex flex-col items-center justify-center text-center">
        <p className="text-sm mb-3">{t("modul_g_requests.not_active")}</p>
        <button onClick={() => navigate("/studio/preview")} className="h-10 px-4 rounded-full bg-[#0A0A0A] text-white text-sm">Zum Enrollment</button>
      </div>
    );
  }

  return (
    <div data-testid="mg-requests-page" className="min-h-screen bg-white">
      <div className="sticky top-0 z-40 flex items-center gap-3 h-14 px-4 border-b border-[#E5E5E5] bg-white">
        <button onClick={() => navigate("/studio/preview")} className="text-[#0A0A0A]" data-testid="mg-requests-back"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[#999]">Modul G · Preview-Requests</p>
          <h1 className="text-base font-bold">Preview-Requests</h1>
        </div>
        <button data-testid="mg-requests-new-btn" onClick={() => setShowCreate(true)} className="h-9 px-3 rounded-full bg-[#0A0A0A] text-white text-xs font-medium">+ Neu</button>
      </div>

      {showCreate && (
        <div data-testid="mg-request-create-panel" className="mx-4 mt-4 border border-[#0A0A0A] p-4 space-y-3">
          <p className="text-sm font-semibold">Neuer Preview-Request</p>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#999]">Pose (Base-Bild)</label>
            {subject && subject.poses?.length ? (
              <div className="grid grid-cols-3 gap-1.5 mt-1">
                {subject.poses.map((p) => (
                  <button key={p.pose_index} data-testid={`mg-request-pose-${p.pose_index}`}
                    onClick={() => setPoseIndex(p.pose_index)}
                    className={`relative border aspect-[3/4] overflow-hidden ${poseIndex === p.pose_index ? "border-[#0A0A0A] ring-2 ring-[#0A0A0A]" : "border-[#E5E5E5]"}`}>
                    <img src={p.url} alt={p.pose_label} className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] px-1 py-0.5 truncate">#{p.pose_index} {p.pose_label || ""}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-red-500 mt-1">Kein Motiv angelegt. <button onClick={() => navigate("/studio/preview/subjects")} className="underline">Motiv anlegen</button></p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#999]">Output</label>
            <div className="flex gap-2 mt-1">
              {["IMAGE", "VIDEO"].map((t) => (
                <button key={t} data-testid={`mg-request-output-${t}`}
                  onClick={() => setOutputType(t)}
                  className={`flex-1 h-9 border text-xs font-medium flex items-center justify-center gap-1 ${outputType === t ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5]"}`}>
                  {t === "IMAGE" ? <ImageIcon size={12} /> : <Video size={12} />} {t}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[#999]">State-Vektor (0.0 – 1.0)</label>
            {AXES.map((a) => (
              <div key={a.key} className="flex items-center gap-3">
                <span className="text-[11px] w-32">{a.label}</span>
                <input data-testid={`mg-request-vec-${a.key}`} type="range" min="0" max="1" step="0.05"
                  value={vec[a.key]}
                  onChange={(e) => setVec((v) => ({ ...v, [a.key]: parseFloat(e.target.value) }))}
                  className="flex-1" />
                <span className="text-[11px] font-mono w-10 text-right">{vec[a.key].toFixed(2)}</span>
              </div>
            ))}
          </div>
          {createError && <p data-testid="mg-request-create-error" className="text-xs text-red-500">{createError}</p>}
          <div className="flex gap-2">
            <PrimaryButton variant="primary" size="sm" testId="mg-request-submit-btn" onClick={submitCreate} disabled>{submitting ? "…" : "Preview anfragen"}</PrimaryButton>
            <button onClick={() => setShowCreate(false)} className="h-10 px-4 rounded-full border border-[#E5E5E5] text-sm">{t("common.cancel")}</button>
          </div>
          <p className="text-[10px] text-[#999]">
            KI-Rendering läuft im Hintergrund (~20 s Zyklus). Ergebnisse werden persistent zwischengespeichert
            und behalten ihre Gültigkeit, bis du das Base-Asset änderst oder löschst.
          </p>
        </div>
      )}

      <div className="p-4 space-y-2">
        {requests.length === 0 && !showCreate && (
          <div data-testid="mg-requests-empty" className="border border-dashed border-[#E5E5E5] p-8 text-center">
            <Sparkles size={32} className="mx-auto text-[#CCC] mb-2" />
            <p className="text-sm text-[#666] mb-1">{t("modul_g_requests.no_requests")}</p>
            <p className="text-xs text-[#999]">{t("modul_g_requests.no_requests_hint")}</p>
          </div>
        )}
        {requests.map((r) => {
          const art = artifacts[r.request_id];
          return (
          <div key={r.request_id} data-testid={`mg-request-card-${r.request_id}`} className="border border-[#E5E5E5] p-3">
            <div className="flex items-center gap-2 mb-2">
              {r.output_type === "IMAGE" ? <ImageIcon size={12} /> : <Video size={12} />}
              <span className="text-[10px] font-mono text-[#999]">{r.request_id.slice(0, 8)}</span>
              <span className={`ml-auto text-xs font-semibold ${STATUS_COLOR[r.status] || "text-[#666]"}`}>{r.status}</span>
            </div>
            <div className="grid grid-cols-5 gap-1 mb-2">
              {AXES.map((a) => (
                <div key={a.key} className="text-center">
                  <p className="text-[9px] text-[#999] truncate">{a.label.slice(0, 5)}</p>
                  <p className="text-[10px] font-mono">{(r.state_vector?.[a.key] || 0).toFixed(2)}</p>
                </div>
              ))}
            </div>
            {r.status === "COMPLETED" && art && (
              <div data-testid={`mg-request-artifact-${r.request_id}`} className="relative mt-2 border border-[#E5E5E5]">
                <img
                  src={art.url}
                  alt="AI-Vorschau"
                  className="w-full max-h-96 object-contain bg-black/5"
                  data-testid={`mg-request-artifact-img-${r.request_id}`}
                />
                <div className="absolute top-2 left-2 bg-black/70 text-white text-[10px] font-semibold px-2 py-1 rounded flex items-center gap-1">
                  <Sparkles size={10} /> KI-generiert
                </div>
                {r.cache_hit && (
                  <div data-testid={`mg-request-cache-hit-${r.request_id}`} className="absolute top-2 right-2 bg-emerald-600 text-white text-[10px] font-semibold px-2 py-1 rounded">
                    Cache-Treffer
                  </div>
                )}
                <p className="text-[10px] text-[#666] p-2">
                  <CheckCircle size={10} className="inline mr-1 text-emerald-600" />
                  Persistent gespeichert · wird erst neu gerendert, wenn du das Asset änderst oder löschst.
                </p>
              </div>
            )}
            {r.status === "RUNNING" && (
              <p className="text-[11px] text-blue-600 flex items-center gap-1 mt-1">
                <Loader2 size={10} className="animate-spin" /> KI rendert dein Motiv…
              </p>
            )}
            {r.status === "FAILED" && r.failure_reason && (
              <p className="text-[10px] text-red-600 flex items-center gap-1 mt-1">
                <AlertCircle size={10} /> {r.failure_reason}
              </p>
            )}
            {["QUEUED", "RUNNING"].includes(r.status) && (
              <button data-testid={`mg-request-cancel-${r.request_id}`} onClick={() => cancelReq(r.request_id)} className="text-[11px] text-red-500 underline mt-1">Stornieren</button>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
